import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { hexToRgba, rgbaToHex } from 'hex-and-rgba/esm';
import arrowDownIcon from 'assets/images/icons/arrow-down.svg';
import boldIcon from 'assets/images/icons/bold.svg';
import crossIcon from 'assets/images/icons/cross.svg';
import italicIcon from 'assets/images/icons/italic.svg';
import strikethroughIcon from 'assets/images/icons/strikethrough.svg';
import underlineIcon from 'assets/images/icons/underline.svg';
import SelectField from 'components/form-components/SelectField';
import { useTranslation } from 'react-i18next';
import CustomColorPicker from './CustomColorPicker';
import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';

const ColorChangeDropdown = ({
  name,
  displayName,
  styleProperties,
  handleChange,
  hideFontSizeOption,
  hideOpacityOption,
  toggleComponent,
  toggleClasses,
}) => {
  const { t } = useTranslation();

  const [dropdownOpen, setDropdownOpen] = useState(false);

  const menuRef = useRef(null);
  const popperRef = useRef(null);

  const handleToggle = (isOpen, e, metadata) => {
    setDropdownOpen(isOpen);
    if (metadata && metadata.popper) {
      popperRef.current = metadata.popper;
    }
  };

  useEffect(() => {
    if (!dropdownOpen || !menuRef.current) return;

    const ro = new ResizeObserver(() => {
      popperRef.current?.update();
    });

    ro.observe(menuRef.current);
    return () => ro.disconnect();
  }, [dropdownOpen]);

  return (
    <div className={'w-100'}>
      {displayName && <label className={'mb-1'}>{t(displayName)}</label>}

      <Dropdown show={dropdownOpen} autoClose='outside' onToggle={handleToggle}>
        <Dropdown.Toggle
          id='dropdown-autoclose-true'
          variant='none'
          className={`${toggleClasses ?? ''}`}
          as='div'
        >
          {toggleComponent}
        </Dropdown.Toggle>

        {dropdownOpen &&
          createPortal(
            <Dropdown.Menu
              ref={menuRef}
              className={`font-style-modal`}
              style={{ zIndex: '1060' }}
              as='div'
              renderOnMount
              popperConfig={{
                modifiers: [
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'flip',
                    options: {
                      boundary: 'viewport',
                    },
                  },
                  {
                    name: 'preventOverflow',
                    options: {
                      boundary: 'viewport',
                      padding: 8,
                    },
                  },
                  {
                    name: 'computeStyles',
                    options: {
                      adaptive: false,
                    },
                  },
                  {
                    name: 'captureInstance',
                    enabled: true,
                    phase: 'write',
                    fn: ({ instance }) => {
                      popperRef.current = instance;
                    },
                  },
                ],
              }}
            >
              <DropdownContent
                name={name}
                styleProperties={styleProperties}
                handleChange={handleChange}
                hideFontSizeOption={hideFontSizeOption}
                hideOpacityOption={hideOpacityOption}
                handleCloseDropdown={() => setDropdownOpen(false)}
              />
            </Dropdown.Menu>,
            document.body,
          )}
      </Dropdown>
    </div>
  );
};

ColorChangeDropdown.propTypes = {
  name: PropTypes.string.isRequired,
  displayName: PropTypes.string,
  styleProperties: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  hideFontSizeOption: PropTypes.bool,
  hideOpacityOption: PropTypes.bool,
  toggleComponent: PropTypes.node.isRequired,
  toggleClasses: PropTypes.string,
};

export default ColorChangeDropdown;

const FontTypeField = ({ name, styleProperties, handleChange }) => {
  const { t } = useTranslation();
  const fontTypes = [
    { value: 'italic', icon: italicIcon },
    { value: 'bold', icon: boldIcon },
    { value: 'underline', icon: underlineIcon },
    { value: 'strikethrough', icon: strikethroughIcon },
  ];

  const handleFontStyleChange = (field, value) => {
    let tempFontTypes = styleProperties[`${name}FontType`]
      ? [...styleProperties[`${name}FontType`]]
      : [];
    if (tempFontTypes.includes(value)) {
      tempFontTypes = tempFontTypes.filter((v) => v !== value);
    } else {
      tempFontTypes.push(value);
    }

    handleChange(field, tempFontTypes);
  };

  return (
    <div className='w-100'>
      <label className='theme-size-0_8'>{t('form_field_font_type')}</label>
      <div className='font-type-field'>
        {fontTypes.map((fontType, index) => (
          <div
            key={index}
            className={`d-flex align-items-center${
              (styleProperties[`${name}FontType`] ?? []).includes(fontType.value)
                ? ' theme-background-green-1 active'
                : ''
            }`}
            onClick={() => handleFontStyleChange([`${name}FontType`], fontType.value)}
          >
            <img
              className={
                (styleProperties[`${name}FontType`] ?? []).includes(fontType.value)
                  ? 'svg-white'
                  : 'svg-black'
              }
              src={fontType.icon}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

FontTypeField.propTypes = {
  name: PropTypes.string.isRequired,
  styleProperties: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
};

function DropdownContent({
  name,
  styleProperties,
  handleChange,
  hideFontSizeOption,
  hideOpacityOption,
  handleCloseDropdown,
}) {
  const { t } = useTranslation();

  const [pickerColor, setPickerColor] = useState({ r: 0, g: 0, b: 0, a: 1 });

  const values = [
    { value: '12px', displayValue: t('ExtraSmall12px'), isNested: false },
    { value: '14px', displayValue: t('Small14px'), isNested: false },
    { value: '16px', displayValue: t('Medium16px'), isNested: false },
    { value: '18px', displayValue: t('Large18px'), isNested: false },
    { value: '20px', displayValue: t('ExtraLarge20px'), isNested: false },
  ];

  const valuesMap = Object.fromEntries(values.map((v) => [v.value, v.displayValue]));

  const handleFontColorChange = (r, g, b, a) => {
    const hexColor = rgbaToHex(r, g, b, !hideOpacityOption ? a : 1);
    handleChange(`${name}Color`, hexColor);
  };

  useEffect(() => {
    const [r, g, b, a] = hexToRgba(styleProperties[`${name}Color`]);
    setPickerColor({ r, g, b, a });
  }, [styleProperties[`${name}Color`]]);

  return (
    <div role='button'>
      <div className='close-btn' onClick={handleCloseDropdown}>
        <img src={crossIcon} />
      </div>
      {!hideFontSizeOption && (
        <div className='d-flex gap-3 align-items-end'>
          <div className='w-50'>
            <SelectField
              placeholder='form_field_font_size'
              label={
                styleProperties[`${name}Size`]
                  ? valuesMap[styleProperties[`${name}Size`]]
                  : 'select_select'
              }
              listValues={values}
              icon={arrowDownIcon}
              handleChange={handleChange}
              selectedValues={[]}
              name={`${name}Size`}
              isAutoClose={true}
              variant='white-1'
              menuClasses={'w-100'}
              shrink
            />
          </div>
          <FontTypeField
            name={name}
            styleProperties={styleProperties}
            handleChange={handleChange}
          />
        </div>
      )}

      <CustomColorPicker pickerColor={pickerColor} handleChange={handleFontColorChange} />
    </div>
  );
}

DropdownContent.propTypes = {
  name: PropTypes.string.isRequired,
  styleProperties: PropTypes.object.isRequired,
  handleChange: PropTypes.func.isRequired,
  hideFontSizeOption: PropTypes.bool,
  hideOpacityOption: PropTypes.bool,
  handleCloseDropdown: PropTypes.func.isRequired,
};
